import { RAZMER_STAGE_ID } from "../components/OSStageTree";
import { RazmerRow, emptyRazmerRow, computeSecondaryQty } from "../components/OSRazmerTable";
import { MatGroup, MatItem } from "../components/OSMaterialnoTable";
import { ProdRow } from "../components/OSProductivityTable";
import { SubprocessItemRow } from "../components/OSSubprocessItemsTable";
import { formatQtyForDisplay, formatUnitPrice, parseDecimalInput } from "./decimalInput";
import { isFinishedProduct } from "./accountingTypeGroup";
import type { SubprocessOption } from "../select/SubprocessCells";

// Shared Ingredient → table-row mappers. OS and RN both paint the same recipe;
// RN then overlays actuals. Inline copies of these maps (especially the RN
// by-item path vs syncIngredientFields) is what produced the dead RN twins.

const unitOf = (it: any): string => {
  const u = it?.mainUnit;
  return u && typeof u === "object" ? (u.code || u.unitMeasurment || u.name || "") : "";
};

const secondaryUnitOf = (it: any): string => {
  const u = it?.secondaryUnit;
  return u && typeof u === "object" ? (u.code || u.unitMeasurment || u.name || "") : "";
};

const fmtPrice = (n: any): string => {
  const v = parseDecimalInput(n);
  return v === null || v === 0 ? "" : formatUnitPrice(v);
};

const refId = (raw: any): string => {
  if (!raw) return "";
  return typeof raw === "object" ? (raw._id || "") : String(raw);
};

export const mapRazmerRows = (saved: any): RazmerRow[] =>
  (Array.isArray(saved) ? saved : []).map((r: any) => {
    const item = typeof r.item === "object" && r.item ? r.item : null;
    const requiredQtyStr = r.requiredQty != null ? formatQtyForDisplay(r.requiredQty) : "";
    const unitCode = unitOf(item);
    return {
      ...emptyRazmerRow(),
      osRowId: r._id ? String(r._id) : "",
      phaseType: refId(r.phaseType),
      priority: r.priority != null ? String(r.priority) : "",
      itemId: item?._id || (typeof r.item === "string" ? r.item : ""),
      ident: item?.ident || "",
      name: item?.name || "",
      requiredQty: requiredQtyStr,
      unitCode,
      secondaryQty: computeSecondaryQty(requiredQtyStr, unitCode) ?? "",
      toleranceLower: r.toleranceLower != null ? String(r.toleranceLower) : "",
      toleranceUpper: r.toleranceUpper != null ? String(r.toleranceUpper) : "",
      allowedWaste: r.allowedWaste != null ? String(r.allowedWaste) : (item?.tercialClass && typeof item.tercialClass === "object" && item.tercialClass.allowedWaste != null ? String(item.tercialClass.allowedWaste) : ""),
      allowedYield: r.allowedYield != null ? String(r.allowedYield) : "",
      allowedRangeManual: r.allowedRangeManual || "",
      sieve: r.sieve || "",
      valuationPrice: item ? fmtPrice(item.valuationPrice) : "",
      lastPurchasePrice: item ? fmtPrice(item.purchasePrice) : "",
      subprocessId: refId(r.subprocess),
    };
  });

// /os opens Размерување ordered by Фаза (the `priority` field; user 2026-10-13); rows without
// Фаза keep their order at the end. Stable, so equal phases keep the saved order. The table
// then groups by process (OSRazmerTable), which is stable too → process, then Фаза.
export const sortRazmerByPhase = (rows: RazmerRow[]): RazmerRow[] => {
  const phaseOf = (row: RazmerRow) => {
    const value = Number(String(row.priority || "").replace(",", "."));
    return row.priority !== "" && Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
  };
  return rows
    .map((row, index) => ({ row, index, phase: phaseOf(row) }))
    .sort((a, b) => (a.phase === b.phase ? a.index - b.index : a.phase < b.phase ? -1 : 1))
    .map(({ row }) => row);
};

export const mapInterProductRows = (saved: any): SubprocessItemRow[] =>
  (Array.isArray(saved) ? saved : []).map((r: any) => {
    const item = typeof r.item === "object" && r.item ? r.item : null;
    return {
      accountingTypeCode: String(item?.accountingType?.code || ''),
      accountingTypeGroup: item?.accountingType?.group || undefined,
      subprocessId: refId(r.subprocess),
      issueSubprocessId: refId(r.issueSubprocess),
      itemId: item?._id || (typeof r.item === "string" ? r.item : ""),
      ident: item?.ident || "",
      name: item?.name || "",
      quantity: r.quantity != null ? formatQtyForDisplay(r.quantity) : "",
      unitCode: unitOf(item),
      secondaryQty: r.secondaryQty != null ? formatQtyForDisplay(r.secondaryQty) : "",
      secondaryUnitCode: secondaryUnitOf(item),
      allowedWaste: r.allowedWaste != null ? String(r.allowedWaste) : "",
      allowedYield: r.allowedYield != null ? String(r.allowedYield) : "",
      // RN "Материјално прием" (7201) needs the subdocument's own _id as its actuals
      // rowKey, plus priority/fromPct/toPct for the read-only reference columns.
      rowId: r._id ? String(r._id) : "",
      priority: r.priority != null ? String(r.priority) : "",
      fromPct: r.fromPct != null ? String(r.fromPct) : "",
      toPct: r.toPct != null ? String(r.toPct) : "",
    };
  });

const processDefinitionOf = (subprocess: SubprocessOption): { id: string; order?: number; name?: string; code?: string } | null => {
  const processDefinition = subprocess.processDefinition;
  if (!processDefinition) return null;
  if (typeof processDefinition === "string") return { id: processDefinition };
  if (!processDefinition._id) return null;
  return {
    id: processDefinition._id,
    order: processDefinition.order,
    name: processDefinition.name,
    code: processDefinition.code,
  };
};

const compareStageOrder = (a: SubprocessOption, b: SubprocessOption): number => {
  const orderA = a.order ?? 0;
  const orderB = b.order ?? 0;
  if (orderA !== orderB) return orderA - orderB;
  const nameCompare = (a.name || "").localeCompare(b.name || "", "mk");
  return nameCompare !== 0 ? nameCompare : a._id.localeCompare(b._id);
};

// Mirrors OSStageTree's visible process/subprocess order for the no-intermediate
// finished-product receipt. Only codebook stages are eligible: until it loads there
// is no visible final stage to receive against.
const resolveFinalRNStage = (ingredient: any, subprocesses: SubprocessOption[]): string => {
  const selectedIds = new Set(
    (Array.isArray(ingredient?.subprocesses) ? ingredient.subprocesses : [])
      .map((subprocess: any) => refId(subprocess))
      .filter(Boolean)
  );
  const selectedStages = subprocesses.filter((subprocess) => selectedIds.has(subprocess._id));
  const byProcessDefinition = new Map<string, { id: string; order?: number; name?: string; code?: string; stages: SubprocessOption[] }>();
  const fallbackStages: SubprocessOption[] = [];

  selectedStages.forEach((stage) => {
    const processDefinition = processDefinitionOf(stage);
    if (!processDefinition) {
      fallbackStages.push(stage);
      return;
    }
    const group = byProcessDefinition.get(processDefinition.id);
    if (group) {
      group.stages.push(stage);
      return;
    }
    byProcessDefinition.set(processDefinition.id, { ...processDefinition, stages: [stage] });
  });

  const groups = Array.from(byProcessDefinition.values()).sort((a, b) => {
    const orderA = typeof a.order === "number" && a.order > 0 ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = typeof b.order === "number" && b.order > 0 ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return (a.name || a.code || "").localeCompare(b.name || b.code || "", "mk");
  });
  const orderedStages = [
    ...groups.flatMap((group) => group.stages.sort(compareStageOrder)),
    ...fallbackStages.sort(compareStageOrder),
  ];
  return orderedStages.at(-1)?._id || "";
};

// The finished product has RN actuals, but is not an OS interProducts row.
export const mapRNReceiptRows = (ingredient: any, subprocesses: SubprocessOption[] = []): SubprocessItemRow[] => {
  const rows = mapInterProductRows(ingredient?.interProducts);
  const last = ingredient?.interProducts?.slice(-1)[0];
  const hasIntermediateReceiptStage = Array.isArray(ingredient?.interProducts)
    && ingredient.interProducts.some((interProduct: any) => !!refId(interProduct?.issueSubprocess));
  const stageId = hasIntermediateReceiptStage
    ? refId(last?.issueSubprocess)
    : resolveFinalRNStage(ingredient, subprocesses);
  const item = ingredient?.items;
  if (stageId && item?._id && isFinishedProduct(item.accountingType)) {
    rows.push(...mapInterProductRows([{
      _id: `finished:${item._id}:${stageId}`,
      subprocess: stageId,
      item,
    }]));
  }
  return rows;
};

export const mapMaterialnoGroups = (saved: any, razmerRows: RazmerRow[]): MatGroup[] =>
  (Array.isArray(saved) ? saved : []).map((g: any, gi: number) => {
    const groupSubprocessId = refId(g.subprocess);
    const items: MatItem[] = Array.isArray(g.items)
      ? g.items.map((it: any) => {
          const item = typeof it.item === "object" && it.item ? it.item : null;
          const itemId = item?._id || (typeof it.item === "string" ? it.item : "");
          const sourceRazmer = razmerRows.find((r) => r.itemId === itemId && r.subprocessId === groupSubprocessId);
          return {
            osRowId: it._id ? String(it._id) : "",
            accountingTypeCode: String(item?.accountingType?.code || ''),
            accountingTypeGroup: item?.accountingType?.group || undefined,
            itemId,
            ident: item?.ident || "",
            name: item?.name || "",
            unitCode: unitOf(item),
            quantity: it.quantity != null ? formatQtyForDisplay(it.quantity) : "",
            priority: sourceRazmer?.priority || "",
          };
        })
      : [];
    // Legacy saved records may carry no priority at all — auto-number by position instead
    // of leaving "Реден бр." (and the mirrored "РБ" column) blank. New groups are no longer
    // numbered this way (Реден бр. is derived from Фаза), so this fallback now only serves
    // old saved records. A manual group's number is its position key (may be fractional,
    // e.g. "1.5"), so never invent one for it — that could collide with a real Фаза number.
    const savedPriority = g.priority != null ? String(g.priority).trim() : "";
    return {
      procedure: g.procedure || "",
      instruction: g.instruction || "",
      priority: !g.manual && savedPriority === "" ? String(gi + 1) : savedPriority,
      items,
      subprocessId: groupSubprocessId,
      manual: !!g.manual,
    };
  });

export const mapProductivityRows = (saved: any): ProdRow[] =>
  (Array.isArray(saved) ? saved : []).map((p: any) => ({
    machineId: typeof p.machine === "object" && p.machine ? p.machine._id : (p.machine || null),
    montageTime: p.montageTime != null ? String(p.montageTime) : "",
    machineTime: p.machineTime != null ? String(p.machineTime) : "",
    cleaningTime: p.cleaningTime != null ? String(p.cleaningTime) : "",
    dismountCleanTime: p.dismountCleanTime != null ? String(p.dismountCleanTime) : "",
    operatorCount: p.operatorCount != null ? String(p.operatorCount) : "",
    setupParams: Array.isArray(p.setupParams)
      ? p.setupParams.map((sp: any) => ({
          description: sp?.description ? String(sp.description) : "",
          par: sp?.par != null ? String(sp.par).replace(/\D/g, "").slice(0, 3) : "",
          var: sp?.var === true || sp?.varX === true || sp?.varJ === true,
        }))
      : [],
    subprocessId: p.isRazmer ? RAZMER_STAGE_ID : refId(p.subprocess),
    ...(p.perSubprocess === true ? { perSubprocess: true } : {}),
    montageStart: p.montageStart != null ? String(p.montageStart) : "",
    montageEnd: p.montageEnd != null ? String(p.montageEnd) : "",
    machineStart: p.machineStart != null ? String(p.machineStart) : "",
    machineEnd: p.machineEnd != null ? String(p.machineEnd) : "",
    dismountStart: p.dismountStart != null ? String(p.dismountStart) : "",
    dismountEnd: p.dismountEnd != null ? String(p.dismountEnd) : "",
    cleaningStart: p.cleaningStart != null ? String(p.cleaningStart) : "",
    cleaningEnd: p.cleaningEnd != null ? String(p.cleaningEnd) : "",
  }));

// OS Ingredient params use `{ entry, value }`. RN actuals use `{ entryId, value }`.
export const mapParamValueMap = (saved: any): Record<string, string> => {
  const mapped: Record<string, string> = {};
  (Array.isArray(saved) ? saved : []).forEach((e: any) => {
    if (!e) return;
    const entryId = e.entryId
      || (typeof e.entry === "object" && e.entry ? e.entry._id : e.entry);
    if (entryId && typeof e.value === "string") mapped[entryId] = e.value;
  });
  return mapped;
};
